import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BookOpen, Clock, Code, Trophy, CheckCircle, Circle, ArrowRight, ChevronRight, Bot, Zap, Lock, Bookmark, Award, Users } from 'lucide-react';
import { courseService } from '../services/courseService';

const CourseView = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    fetchCourseAndSyllabus();
  }, [courseId]);
  
  const fetchCourseAndSyllabus = async () => {
    try {
      setIsLoading(true);
      const [syllabusData, projectsData] = await Promise.all([
        courseService.getCourseSyllabus(courseId),
        courseService.getCourseProjects(courseId),
      ]);
      setCourse(syllabusData.course);
      setLessons(syllabusData.lessons);
      setProjects(projectsData.projects);
    } catch (error) {
      toast.error('Failed to load course');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Course Not Found</h1>
          <Link to="/courses" className="btn-primary">
            Browse Courses
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Hero Section */}
      <div className="bg-black dark:bg-gray-950 text-white py-20 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <ChevronRight size={16} />
            <Link to="/courses" className="hover:text-primary-500 transition-colors">Courses</Link>
            <ChevronRight size={16} />
            <span className="text-white">{course.title}</span>
          </div>
          
          {/* Badge Row */}
          <div className="flex items-center gap-3 mb-6">
            <span className="badge bg-primary-500 text-black capitalize font-bold">
              {course.difficulty_level}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-300 font-medium">{course.duration_hours} hours total</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-300 font-medium flex items-center gap-1">
              <Users size={16} />
              4,500+ enrolled
            </span>
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            Master {course.title.split(' ').slice(1).join(' ')} and build <span className="text-highlight">amazing</span> projects
          </h1>
          
          {/* Description */}
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl leading-relaxed">
            {course.description}
          </p>
          
          {/* Stats Row */}
          <div className="flex items-center gap-8 mb-8 text-base">
            <div className="flex items-center gap-2">
              <BookOpen size={24} className="text-primary-500" />
              <span className="font-medium">{course.total_lessons} Lessons</span>
            </div>
            <div className="flex items-center gap-2">
              <Code size={24} className="text-primary-500" />
              <span className="font-medium">{course.total_projects} Projects</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy size={24} className="text-primary-500" />
              <span className="font-medium">{course.total_quizzes} Quizzes</span>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to={`/lessons/${lessons[0]?.lesson_id}`}
              className="btn-primary text-lg inline-flex items-center justify-center"
            >
              Start Learning Now
              <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link 
              to={`/lessons/${lessons[0]?.lesson_id}`}
              className="btn-outline text-lg inline-flex items-center justify-center"
            >
              Preview First Lesson
            </Link>
          </div>
        </div>
      </div>
      
      {/* Course Stats Bar */}
      <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Progress */}
            <div className="bg-white dark:bg-gray-950 rounded-xl p-5 border border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Your Progress</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-3">0% Complete</div>
              <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2">
                <div className="bg-primary-500 h-2 rounded-full" style={{width: '0%'}}></div>
              </div>
            </div>
            
            {/* Time Investment */}
            <div className="bg-white dark:bg-gray-950 rounded-xl p-5 border border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Time Investment</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Clock size={24} className="text-primary-500" />
                ~{course.duration_hours}h
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">At your own pace</div>
            </div>
            
            {/* Difficulty */}
            <div className="bg-white dark:bg-gray-950 rounded-xl p-5 border border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Difficulty Level</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 capitalize">{course.difficulty_level}</div>
              <div className="flex gap-1">
                {[1, 2, 3].map((level) => (
                  <div 
                    key={level}
                    className={`h-2 flex-1 rounded-full ${
                      level <= (course.difficulty_level === 'beginner' ? 1 : course.difficulty_level === 'intermediate' ? 2 : 3)
                        ? 'bg-primary-500' 
                        : 'bg-gray-200 dark:bg-gray-800'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
            
            {/* AI Support */}
            <div className="bg-white dark:bg-gray-950 rounded-xl p-5 border border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">AI Support</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Bot size={24} className="text-primary-500" />
                24/7
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Always available</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* What You'll Learn */}
            {course.learning_objectives && course.learning_objectives.length > 0 && (
              <div className="bg-white dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
                <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">What You'll Learn</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {course.learning_objectives.map((objective, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{objective}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Course Syllabus */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
              <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Course Syllabus</h2>
              <div className="space-y-3">
                {lessons.map((lesson, index) => (
                  <Link
                    key={lesson.lesson_id}
                    to={`/lessons/${lesson.lesson_id}`}
                    className="block p-5 rounded-lg border-2 border-gray-200 dark:border-gray-800 hover:border-primary-500 hover:shadow-md transition-all duration-200 group bg-white dark:bg-gray-950"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                          index === 0 ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-800'
                        } flex items-center justify-center mr-4`}>
                          <span className={`text-sm font-bold ${
                            index === 0 ? 'text-black' : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors">
                            {lesson.title}
                          </h3>
                          {lesson.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {lesson.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 ml-4">
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                          <Clock size={14} className="mr-1" />
                          {lesson.duration_minutes}m
                        </div>
                        {index === 0 ? (
                          <Circle size={20} className="text-gray-300 dark:text-gray-600" />
                        ) : (
                          <Lock size={20} className="text-gray-400 dark:text-gray-600" />
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Projects Section */}
            {projects.length > 0 && (
              <div className="bg-white dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
                <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Projects</h2>
                <div className="space-y-4">
                  {projects.map((project) => (
                    <Link
                      key={project.project_id}
                      to={`/projects/${project.project_id}`}
                      className="block p-6 rounded-xl border border-gray-200 dark:border-gray-800 hover:border-primary-500 hover:shadow-lg transition-all duration-200 group bg-black dark:bg-gray-900"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start flex-1">
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary-500 flex items-center justify-center mr-4">
                            <Code size={24} className="text-black" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-white group-hover:text-primary-500 transition-colors mb-2">
                              {project.title}
                            </h3>
                            <p className="text-sm text-gray-400 mb-3 leading-relaxed">
                              {project.description}
                            </p>
                            <div className="flex items-center gap-3 text-xs">
                              <span className={`px-3 py-1 rounded-full font-semibold ${
                                project.difficulty_level === 'beginner' ? 'bg-green-500 text-black' :
                                project.difficulty_level === 'intermediate' ? 'bg-yellow-500 text-black' :
                                'bg-red-500 text-white'
                              }`}>
                                {project.difficulty_level}
                              </span>
                              <span className="text-gray-400 flex items-center font-medium">
                                <Clock size={14} className="mr-1" />
                                {project.estimated_duration_hours}h estimated
                              </span>
                            </div>
                          </div>
                        </div>
                        <Trophy size={28} className="text-primary-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Column - Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              {/* Quick Info Card */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                {/* Course Icon */}
                <div className="bg-black dark:bg-gray-950 rounded-xl h-32 flex items-center justify-center mb-6">
                  <Code size={64} className="text-primary-500" />
                </div>
                
                <h3 className="font-bold text-lg mb-6 text-gray-900 dark:text-white">Course Details</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Difficulty Level</div>
                    <span className="badge bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 capitalize">
                      {course.difficulty_level}
                    </span>
                  </div>
                  
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Duration</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{course.duration_hours} hours</div>
                  </div>
                  
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Content</div>
                    <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <BookOpen size={16} className="text-primary-500" />
                        {course.total_lessons} lessons
                      </div>
                      <div className="flex items-center gap-2">
                        <Code size={16} className="text-primary-500" />
                        {course.total_projects} projects
                      </div>
                      <div className="flex items-center gap-2">
                        <Trophy size={16} className="text-primary-500" />
                        {course.total_quizzes} quizzes
                      </div>
                    </div>
                  </div>
                  
                  {course.tags && course.tags.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Topics</div>
                      <div className="flex flex-wrap gap-2">
                        {course.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* CTAs */}
                <Link
                  to={`/lessons/${lessons[0]?.lesson_id}`}
                  className="btn-primary w-full text-center inline-block mb-3"
                >
                  Start Course
                </Link>
                <button className="btn-ghost w-full inline-flex items-center justify-center text-gray-700 dark:text-gray-300">
                  <Bookmark size={18} className="mr-2" />
                  Save for Later
                </button>
              </div>
              
              {/* AI Assistant Card */}
              <div className="bg-white dark:bg-black rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
                    <Bot size={24} className="text-black" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white">AI-Powered Learning</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 leading-relaxed">
                  Get instant help and explanations from Claude AI whenever you're stuck or need guidance throughout the course.
                </p>
                <span className="badge bg-primary-500 text-black">
                  Get Help Anytime
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white tracking-tight">
              What's Included in This Course
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Everything you need to succeed in your learning journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Code, title: 'Interactive Lessons', description: 'Write and run code directly in your browser with real-time feedback and instant validation.' },
              { icon: Award, title: 'Real Projects', description: 'Build portfolio-worthy projects that demonstrate your skills to potential employers.' },
              { icon: Bot, title: 'AI Assistance', description: 'Get help anytime with Claude AI for explanations, debugging, and personalized guidance.' },
              { icon: Trophy, title: 'Knowledge Quizzes', description: 'Test your understanding with interactive quizzes after each major concept.' },
              { icon: Zap, title: 'Hands-On Practice', description: 'Learn by doing with hundreds of coding exercises and challenges.' },
              { icon: BookOpen, title: 'Comprehensive Content', description: 'Well-structured curriculum covering everything from basics to advanced topics.' }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-black dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800 group hover:border-primary-500 transition-all duration-200">
                  <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="text-black" size={32} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white text-center">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-center text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-black dark:bg-gray-950 border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-black mb-8 text-white">
            Ready to start <span className="text-highlight">learning</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Begin your journey today and master Python with AI-powered guidance. It's completely free!
          </p>
          <Link 
            to={`/lessons/${lessons[0]?.lesson_id}`}
            className="btn-primary text-lg inline-flex items-center justify-center"
          >
            Begin First Lesson
            <ArrowRight className="ml-2" size={22} />
          </Link>
          
          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-white">✓ 100% Free</div>
              <div className="text-gray-400 text-sm">Complete access to all content</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-white">✓ AI Support Included</div>
              <div className="text-gray-400 text-sm">Get help whenever you need it</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-white">✓ Learn at Your Pace</div>
              <div className="text-gray-400 text-sm">No deadlines or pressure</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseView;

